import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Accordion, Form, Button, Spinner, ProgressBar, Badge } from 'react-bootstrap';
import axios from 'axios';
import { showToast as toast } from '../toastManager';
import { processAssetsAndGenerateTemplates, getMinimalSectionCSS } from '../utils/dynamicTemplateEngine';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getYouTubeId(url) {
  const m = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^&?/]+)/);
  return m ? m[1] : null;
}

function getYouTubeThumbnail(url) {
  const id = getYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
}

const isYouTubeUrl = (u) => !!(u && (u.includes('youtube.com') || u.includes('youtu.be')));

// Poll a background job using the shared /api/firebase/job_status/:jobId
// endpoint. Mirrors the cadence used by Run Modules (1.5 s × 200 ≈ 5 min).
// onProgress receives the latest job object after every poll so the caller
// can drive a progress bar.
async function pollJob(app_url, jobId, onProgress) {
  for (let i = 0; i < 200; i++) {
    let job;
    try {
      const res = await axios.get(`${app_url}/api/firebase/job_status/${jobId}`, { withCredentials: true });
      job = res.data?.job;
    } catch (err) {
      if (i > 2) throw new Error('Lost connection to job status'); // tolerate 1-2 transient errors
      await new Promise(r => setTimeout(r, 1500));
      continue;
    }
    if (!job) throw new Error('Job not found');
    if (typeof onProgress === 'function') onProgress(job);
    if (job.status === 'succeeded') return job.result;
    if (job.status === 'failed') throw new Error(job.error?.message || 'Job failed');
    await new Promise(r => setTimeout(r, 1500));
  }
  throw new Error('Job polling timed out');
}

// ─── Component ──────────────────────────────────────────────────────────────

const SectionApproval = ({ ViewedPage, shop_domain, app_url }) => {
  useEffect(() => { if (ViewedPage) ViewedPage(57); }, []);

  // ── Common ────────────────────────────────────────────────────────────
  const [stateLoaded, setStateLoaded] = useState(false);
  const [sectionApprovalState, setSectionApprovalState] = useState({ website_sections: {}, youtube_sections: [] });

  // ── Website ───────────────────────────────────────────────────────────
  const [websitePages, setWebsitePages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [websiteSections, setWebsiteSections] = useState([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState(new Set());
  const [websiteFetchStatus, setWebsiteFetchStatus] = useState('idle'); // 'idle' | 'loading' | 'done' | 'error'
  const [websiteImportStatus, setWebsiteImportStatus] = useState('idle');
  const [websiteFetchMessage, setWebsiteFetchMessage] = useState('');
  const [websiteImportMessage, setWebsiteImportMessage] = useState('');

  // ── YouTube ───────────────────────────────────────────────────────────
  const [ytVideos, setYtVideos] = useState([]);
  const [selectedYtIds, setSelectedYtIds] = useState(new Set());
  const [ytImportStatus, setYtImportStatus] = useState('idle');
  const [ytProgress, setYtProgress] = useState({ count: 0, total: 0 });
  // IDs of videos that have been "activated" — their card swaps the thumbnail
  // for a real YouTube iframe so the user can hit play without leaving the
  // page. Activation is per-card and never reverts in this session.
  const [playingIds, setPlayingIds] = useState(new Set());

  // Hidden DOM root used to extract rendered HTML from the template generator.
  const ytStagingRef = useRef(null);

  // ─── Load on mount ────────────────────────────────────────────────────

  useEffect(() => {
    if (!shop_domain || !app_url) return;
    (async () => {
      try {
        const [pagesRes, assetsRes, stateRes] = await Promise.all([
          axios.get(`${app_url}/api/settings/domains-and-urls/selected-pages`, { withCredentials: true }),
          axios.post(`${app_url}/api/assets/get_all`, { shop_domain }, { withCredentials: true }),
          axios.get(`${app_url}/api/dev/section-approval/state`, { params: { shop: shop_domain }, withCredentials: true }),
        ]);

        if (pagesRes.data?.success) {
          // /domains-and-urls/selected-pages returns the fields flat on the
          // response body — not wrapped in a `data` envelope like other endpoints.
          const wp      = pagesRes.data.website_pages          || [];
          const lp      = pagesRes.data.landing_pages          || [];
          const fetched = pagesRes.data.fetched_website_pages  || [];
          // Prefer explicitly saved website_pages; fall back to fetched/LP for usability.
          const merged = [...new Set(wp)].filter(Boolean);
          setWebsitePages(merged);
        }

        if (assetsRes.data?.success) {
          const all = assetsRes.data.data || [];
          const yt = all.filter(a =>
            (a.type === 'Video' || a.type === 'Video Link') &&
            isYouTubeUrl(a.url || a.videoUrl || a.fileUrl)
          );
          setYtVideos(yt);
        }

        if (stateRes.data?.success) {
          const data = stateRes.data.data || {};
          setSectionApprovalState({
            website_sections: data.website_sections || {},
            youtube_sections: Array.isArray(data.youtube_sections) ? data.youtube_sections : [],
          });
        }
      } catch (err) {
        console.error('SectionApproval load failed', err);
        toast.error('Failed to load Section Approval data');
      } finally {
        setStateLoaded(true);
      }
    })();
  }, [shop_domain, app_url]);

  // When the user changes the selected page, hydrate sections + selection from
  // cache so they don't have to re-fetch every time.
  useEffect(() => {
    if (!selectedPage) {
      setWebsiteSections([]);
      setSelectedSectionIds(new Set());
      setWebsiteFetchStatus('idle');
      setWebsiteFetchMessage('');
      return;
    }
    const cached = sectionApprovalState.website_sections?.[selectedPage];
    if (cached?.sections?.length) {
      setWebsiteSections(cached.sections);
      setWebsiteFetchStatus('done');
      setWebsiteFetchMessage(`Loaded ${cached.sections.length} cached section(s) from ${new Date(cached.fetched_at).toLocaleString()}`);
    } else {
      setWebsiteSections([]);
      setWebsiteFetchStatus('idle');
      setWebsiteFetchMessage('No cached sections — click Fetch Sections.');
    }
    setSelectedSectionIds(new Set());
  }, [selectedPage, sectionApprovalState]);

  // ─── Website handlers ─────────────────────────────────────────────────

  const handleFetchWebsiteSections = async (force = false) => {
    if (!selectedPage) { toast.error('Select a website page first'); return; }
    setWebsiteFetchStatus('loading');
    setWebsiteFetchMessage('Submitting fetch job...');
    try {
      const res = await axios.post(`${app_url}/api/dev/section-approval/website/fetch`,
        { shop: shop_domain, page_url: selectedPage, force },
        { withCredentials: true }
      );

      if (res.data?.cached) {
        const sections = res.data.data?.sections || [];
        setWebsiteSections(sections);
        setSectionApprovalState(prev => ({
          ...prev,
          website_sections: { ...prev.website_sections, [selectedPage]: { sections, fetched_at: res.data.data.fetched_at } },
        }));
        setWebsiteFetchStatus('done');
        setWebsiteFetchMessage(`Loaded ${sections.length} cached section(s)`);
        return;
      }

      const jobId = res.data?.jobId;
      if (!jobId) throw new Error('Server did not return a jobId');

      setWebsiteFetchMessage('Running section-JSON automation...');
      const result = await pollJob(app_url, jobId, (job) => {
        const last = (job.progress || []).slice(-1)[0];
        if (last?.message) setWebsiteFetchMessage(last.message);
      });

      const sections = result?.sections || [];
      setWebsiteSections(sections);
      setSectionApprovalState(prev => ({
        ...prev,
        website_sections: { ...prev.website_sections, [selectedPage]: { sections, fetched_at: new Date().toISOString() } },
      }));
      setWebsiteFetchStatus('done');
      setWebsiteFetchMessage(`Fetched ${sections.length} section(s)`);
      toast.success(`Fetched ${sections.length} section(s)`);
    } catch (err) {
      console.error('handleFetchWebsiteSections error', err);
      setWebsiteFetchStatus('error');
      setWebsiteFetchMessage(err.message || 'Fetch failed');
      toast.error(err.message || 'Fetch failed');
    }
  };

  const toggleSectionSelected = (id) => {
    setSelectedSectionIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleImportWebsiteSections = async () => {
    if (!selectedPage || selectedSectionIds.size === 0) {
      toast.error('Select at least one section to import');
      return;
    }
    setWebsiteImportStatus('loading');
    setWebsiteImportMessage('Submitting import job...');
    try {
      const res = await axios.post(`${app_url}/api/dev/section-approval/website/import`,
        { shop: shop_domain, page_url: selectedPage, selected_section_ids: Array.from(selectedSectionIds) },
        { withCredentials: true }
      );
      const jobId = res.data?.jobId;
      if (!jobId) throw new Error('Server did not return a jobId');

      const result = await pollJob(app_url, jobId, (job) => {
        const last = (job.progress || []).slice(-1)[0];
        if (last?.message) setWebsiteImportMessage(last.message);
      });

      const importedCount = (result?.imported || []).length;
      setWebsiteImportStatus('done');
      setWebsiteImportMessage(`Imported ${importedCount} section(s)`);
      toast.success(`Imported ${importedCount} section(s)`);
      setSelectedSectionIds(new Set());
    } catch (err) {
      console.error('handleImportWebsiteSections error', err);
      setWebsiteImportStatus('error');
      setWebsiteImportMessage(err.message || 'Import failed');
      toast.error(err.message || 'Import failed');
    }
  };

  // ─── YouTube handlers ─────────────────────────────────────────────────

  const ytAlreadyCreatedByAsset = useMemo(() => {
    const m = new Map();
    for (const rec of sectionApprovalState.youtube_sections || []) {
      if (rec.asset_id) m.set(String(rec.asset_id), rec);
    }
    return m;
  }, [sectionApprovalState.youtube_sections]);

  const toggleYtSelected = (id) => {
    setSelectedYtIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleCreateYoutubeSections = async () => {
    if (selectedYtIds.size === 0) { toast.error('Select at least one video'); return; }

    setYtImportStatus('loading');
    setYtProgress({ count: 0, total: selectedYtIds.size });

    try {
      // Generate HTML for each selected video using the same engine AssetsSection.jsx uses.
      const sections = [];
      for (const id of selectedYtIds) {
        const asset = ytVideos.find(v => String(v._id) === String(id));
        if (!asset) continue;
        // The template engine derefs `aiResponse.X` unguarded (sections, headline,
        // description, faq, pdf_faq_sections). Pass {} so every access falls
        // through to the built-in defaults — we have no real AI response for a
        // plain YouTube embed.
        const generated = processAssetsAndGenerateTemplates([asset], 'SectionApproval', 'youtube section', {}, app_url);
        // The engine returns { templates: [DOM nodes...], previewUrls: [{section_id, section_name, section_type, ...}], totalSections }.
        // Templates are real DOM elements — read .outerHTML for the payload; metadata
        // comes from the parallel previewUrls array (same convention AssetsSection.jsx uses).
        console.log(generated);
        const templates    = generated?.templates    || [];
        const previewUrls  = generated?.previewUrls  || [];
        const idx = templates.findIndex(t => t && t.outerHTML);
        if (idx < 0) {
          console.warn('No template generated for asset', asset);
          continue;
        }
        const domEl = templates[idx];
        const meta  = previewUrls[idx] || {};
        // Engine returns the same `single_video_0` for every call (its
        // templateIndex resets per invocation), so we derive a stable, unique
        // id from the source asset to avoid the Firebase Cloud Function
        // version-bumping one imported_sections row N times.
        const sectionId   = `yt_${String(asset._id)}`;
        const sectionName = meta.section_name || asset.title || 'YouTube Section';
        // Inline the section's minimal CSS so the uploaded file at file_url is a
        // self-contained, renderable HTML document. uploadSectionToFirebase
        // hardcodes css_content: '' on the server side, so this is the only
        // way to ship styles to the Cloud Function.
        const css = getMinimalSectionCSS('single-video') || '';
        const safeTitle = (sectionName || 'Section').replace(/[<>&"]/g, '');
        const html_content =
          `<!DOCTYPE html><html lang="en"><head>` +
          `<meta charset="utf-8">` +
          `<meta name="viewport" content="width=device-width,initial-scale=1">` +
          `<title>${safeTitle}</title>` +
          `<style>${css}</style>` +
          `</head><body style="margin:0;padding:0">${domEl.outerHTML}</body></html>`;
        sections.push({
          asset_id:     String(asset._id),
          video_url:    asset.url || asset.videoUrl || asset.fileUrl || '',
          section_id:   sectionId,
          section_name: sectionName,
          html_content,
          css_content:  '',
        });
      }

      if (sections.length === 0) {
        throw new Error('No sections could be generated from the selected videos');
      }

      const res = await axios.post(`${app_url}/api/dev/section-approval/youtube/import`,
        { shop: shop_domain, sections },
        { withCredentials: true }
      );
      const jobId = res.data?.jobId;
      if (!jobId) throw new Error('Server did not return a jobId');

      const result = await pollJob(app_url, jobId, (job) => {
        if (typeof job.progress_count === 'number' && typeof job.progress_total === 'number') {
          setYtProgress({ count: job.progress_count, total: job.progress_total });
        }
      });

      const created = result?.created || [];
      setSectionApprovalState(prev => ({
        ...prev,
        youtube_sections: [...(prev.youtube_sections || []), ...created],
      }));
      setYtImportStatus('done');
      setYtProgress({ count: created.length, total: created.length });
      setSelectedYtIds(new Set());
      toast.success(`Created ${created.length} YouTube section(s)`);
    } catch (err) {
      console.error('handleCreateYoutubeSections error', err);
      setYtImportStatus('error');
      toast.error(err.message || 'YouTube import failed');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────

  if (!stateLoaded) {
    return <div className="text-center py-5"><Spinner animation="border" /></div>;
  }

  return (
    <div>
      <Accordion alwaysOpen defaultActiveKey={['website', 'youtube']}>
        <Accordion.Item eventKey="website">
          <Accordion.Header>Website Section</Accordion.Header>
          <Accordion.Body>
            <Form.Group className="mb-3">
              <Form.Label>Website Page</Form.Label>
              <Form.Select
                value={selectedPage}
                onChange={(e) => setSelectedPage(e.target.value)}
                disabled={websiteFetchStatus === 'loading' || websiteImportStatus === 'loading'}
              >
                <option value="">Select a page…</option>
                {websitePages.map(p => <option key={p} value={p}>{p}</option>)}
              </Form.Select>
              {websitePages.length === 0 && (
                <Form.Text className="text-muted">
                  No website pages saved yet — set them in Settings → Domains &amp; URLs first.
                </Form.Text>
              )}
            </Form.Group>

            <div className="d-flex gap-2 mb-3">
              <Button
                className="sxi_primary_blue_btn"
                onClick={() => handleFetchWebsiteSections(false)}
                disabled={!selectedPage || websiteFetchStatus === 'loading'}
              >
                {websiteFetchStatus === 'loading' ? <><Spinner size="sm" animation="border" /> Fetching…</> : 'Fetch Sections'}
              </Button>
              {websiteSections.length > 0 && (
                <Button
                  variant="outline-secondary"
                  onClick={() => handleFetchWebsiteSections(true)}
                  disabled={websiteFetchStatus === 'loading'}
                  title="Ignore cache and re-run the automation"
                >
                  Re-fetch
                </Button>
              )}
            </div>

            {websiteFetchMessage && (
              <div className="small text-muted mb-3">{websiteFetchMessage}</div>
            )}

            {websiteSections.length > 0 && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>{websiteSections.length} section(s) detected</strong>
                  <span className="text-muted small">{selectedSectionIds.size} selected</span>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  {websiteSections.map(section => {
                    const isSelected = selectedSectionIds.has(section.section_id);
                    return (
                      <div
                        key={section.section_id}
                        onClick={() => toggleSectionSelected(section.section_id)}
                        style={{
                          border: isSelected ? '2px solid #132848' : '1px solid #dee2e6',
                          background: isSelected ? '#eef2f6' : '#fff',
                          borderRadius: 8,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                      >
                        <div style={{ height: 140, background: '#f0f2f5', overflow: 'hidden' }}>
                          {section.image_url ? (
                            <img
                              src={section.image_url}
                              alt={section.section_name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9aa2ad' }}>
                              No preview
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <div style={{
                            position: 'absolute', top: 6, right: 6,
                            background: '#132848', color: '#fff',
                            borderRadius: '50%', width: 22, height: 22,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700,
                          }}>✓</div>
                        )}
                        <div style={{ padding: '6px 8px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {section.section_name || section.section_id}
                          </div>
                          {section.section_category && (
                            <div style={{ fontSize: 10, color: '#6c757d' }}>{section.section_category}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="d-flex align-items-center gap-3">
                  <Button
                    className="sxi_primary_blue_btn"
                    onClick={handleImportWebsiteSections}
                    disabled={selectedSectionIds.size === 0 || websiteImportStatus === 'loading'}
                  >
                    {websiteImportStatus === 'loading'
                      ? <><Spinner size="sm" animation="border" /> Importing…</>
                      : `Import Selected (${selectedSectionIds.size})`}
                  </Button>
                  {websiteImportMessage && <span className="small text-muted">{websiteImportMessage}</span>}
                </div>
              </>
            )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="youtube">
          <Accordion.Header>YouTube Section</Accordion.Header>
          <Accordion.Body>
            {ytVideos.length === 0 ? (
              <div className="text-muted small">
                No YouTube video assets found. Add some in Settings → Assets first.
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>{ytVideos.length} YouTube video(s) available</strong>
                  <span className="text-muted small">{selectedYtIds.size} selected</span>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 12,
                    marginBottom: 16,
                    maxHeight: "85vh",
                    overflow: "auto"
                  }}
                >
                  {ytVideos.map(v => {
                    const id = String(v._id);
                    const isSelected = selectedYtIds.has(id);
                    const alreadyCreated = ytAlreadyCreatedByAsset.has(id);
                    const sourceUrl = v.url || v.videoUrl || v.fileUrl;
                    const ytId  = getYouTubeId(sourceUrl);
                    const thumb = ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg` : null;
                    const isPlaying = playingIds.has(id);

                    const activatePlayer = (e) => {
                      // Don't let the player click also toggle selection on the
                      // surrounding card.
                      e.stopPropagation();
                      setPlayingIds(prev => {
                        const next = new Set(prev);
                        next.add(id);
                        return next;
                      });
                    };

                    return (
                      <div
                        key={id}
                        onClick={() => toggleYtSelected(id)}
                        style={{
                          border: isSelected ? '2px solid #132848' : '1px solid #dee2e6',
                          background: isSelected ? '#eef2f6' : '#fff',
                          borderRadius: 8,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                      >
                        <div
                          style={{ height: 180, background: '#f0f2f5', overflow: 'hidden', position: 'relative' }}
                          onClick={isPlaying ? (e) => e.stopPropagation() : undefined}
                        >
                          {isPlaying && ytId ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
                              title={v.title || 'YouTube video'}
                              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                              allowFullScreen
                              style={{ width: '100%', height: '100%', border: 0 }}
                            />
                          ) : thumb ? (
                            <>
                              <img src={thumb} alt={v.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <button
                                type="button"
                                onClick={activatePlayer}
                                aria-label={`Play ${v.title || 'video'}`}
                                style={{
                                  position: 'absolute',
                                  top: '50%', left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: 48, height: 48,
                                  border: 0,
                                  borderRadius: '50%',
                                  background: 'rgba(229, 9, 20, 0.92)',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                                }}
                              >
                                <span style={{
                                  display: 'inline-block',
                                  width: 0, height: 0,
                                  borderLeft: '14px solid #fff',
                                  borderTop: '9px solid transparent',
                                  borderBottom: '9px solid transparent',
                                  marginLeft: 4,
                                }} />
                              </button>
                            </>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 28 }}>🎬</div>
                          )}
                        </div>
                        {isSelected && (
                          <div style={{
                            position: 'absolute', top: 6, right: 6,
                            background: '#132848', color: '#fff',
                            borderRadius: '50%', width: 22, height: 22,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700,
                          }}>✓</div>
                        )}
                        {alreadyCreated && (
                          <Badge bg="success" style={{ position: 'absolute', top: 6, left: 6 }}>Created</Badge>
                        )}
                        <div style={{ padding: '6px 8px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {v.title}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="d-flex align-items-center gap-3 mb-2">
                  <Button
                    className="sxi_primary_blue_btn"
                    onClick={handleCreateYoutubeSections}
                    disabled={selectedYtIds.size === 0 || ytImportStatus === 'loading'}
                  >
                    {ytImportStatus === 'loading'
                      ? <><Spinner size="sm" animation="border" /> Creating…</>
                      : `Create Sections (${selectedYtIds.size})`}
                  </Button>
                  {ytImportStatus === 'loading' && ytProgress.total > 0 && (
                    <div style={{ flex: 1, maxWidth: 320 }}>
                      <ProgressBar
                        now={(ytProgress.count / ytProgress.total) * 100}
                        label={`${ytProgress.count} / ${ytProgress.total}`}
                      />
                    </div>
                  )}
                </div>

                {sectionApprovalState.youtube_sections?.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-muted small">
                      Already created ({sectionApprovalState.youtube_sections.length})
                    </summary>
                    <ul className="small text-muted mt-2 mb-0">
                      {sectionApprovalState.youtube_sections.map((s, idx) => (
                        <li key={`${s.section_id}-${idx}`}>
                          {s.section_name || s.section_id}
                          {s.file_url && (
                            <> — <a href={s.file_url} target="_blank" rel="noopener noreferrer">open</a></>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            )}

            {/* Hidden staging div used by processAssetsAndGenerateTemplates when it
                returns DOM nodes rather than HTML strings. */}
            <div ref={ytStagingRef} style={{ display: 'none' }} />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default SectionApproval;
